import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LogIn } from 'lucide-react';
import { pickLandingPath } from '../config/modules';
import WelcomeOverlay from '../components/WelcomeOverlay';

export default function LoginPage() {
  const { user, login, tenantFeatures, tenantFeaturesLoading } = useAuth();
  const { logoUrl, platformName, tagline, loginWelcomeText, loginUseOutletLogo, loginOutletId } = useBranding();
  const location = useLocation();
  // iter-loyalty: return-to logic — when a deep-linked protected route (eg. the
  // Loyalty manager QR at /loyalty/:slug/manager) redirects here for auth, we
  // preserve the intended URL in `location.state.from` and route back to it
  // after login instead of dropping the user on their default dashboard.
  const returnToPath = (location.state && location.state.from && location.state.from.pathname
    ? location.state.from.pathname + (location.state.from.search || '')
    : null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [outletLogo, setOutletLogo] = useState(null);
  // iter179 — once the welcome overlay completes, we redirect to its chosen path
  // (which may be the explicit tenant landing_module or null → use computed chain).
  const [overlayDone, setOverlayDone] = useState(false);
  const [overlayTarget, setOverlayTarget] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    if (loginUseOutletLogo && loginOutletId) {
      axios.get(`${API}/branding/outlet/${loginOutletId}`)
        .then(res => { if (res.data.has_logo) setOutletLogo(`${API}/outlets/${loginOutletId}/logo`); })
        .catch(() => {});
    }
  }, [API, loginUseOutletLogo, loginOutletId]);

  const displayLogo = outletLogo || logoUrl;

  if (user) {
    if (user.role === 'super_admin') return <Navigate to="/super/dashboard" replace />;
    if (user.role === 'production_head') return <Navigate to="/admin/recipes" replace />;
    // iter204 — Studio users (consultant-only product) bypass the welcome
    // overlay entirely and land directly on the EI workspace.
    if (user.role === 'studio_user') return <Navigate to="/admin/expansion-intelligence" replace />;
    // iter-loyalty: if user came here from a specific protected URL (deep link),
    // send them back there after login instead of the default landing.
    if (returnToPath) return <Navigate to={returnToPath} replace />;
    // iter177 — Wait for the tenant feature flags before redirecting so we
    // don't land a restricted tenant on Dashboard when Dashboard is disabled.
    if (tenantFeaturesLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
          <div className="animate-spin w-7 h-7 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      );
    }
    // iter179 — Welcome Experience Layer. The overlay fetches /api/tenant/welcome,
    // honors the tenant's overlay_mode (every_login | daily | disabled), and on
    // completion calls onComplete(target). If overlay is disabled or already-seen
    // today, it self-skips by calling onComplete(landing_path) immediately —
    // user lands on their workspace with zero visual delay.
    if (!overlayDone) {
      return (
        <WelcomeOverlay
          onComplete={(target) => {
            setOverlayTarget(target);
            setOverlayDone(true);
          }}
        />
      );
    }
    // Prefer the explicit tenant landing chosen by the overlay's backend payload;
    // otherwise fall through to the safety chain (with RestrictedHome fallback).
    const landing = overlayTarget || pickLandingPath(user, tenantFeatures);
    return <Navigate to={landing || '/'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 login-bg relative">
        <div className="absolute inset-0 bg-[color:var(--color-primary)]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img src={displayLogo} alt={platformName} className="w-48 mb-4 object-contain" />
          <p className="text-sm opacity-80 italic mb-3">{tagline}</p>
          <p className="text-lg opacity-90 max-w-md leading-relaxed" data-testid="login-welcome-text">
            {loginWelcomeText}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#F9F9F8]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={displayLogo} alt={platformName} className="h-14 w-auto object-contain" />
          </div>

          <Card className="border-[#E2E8DF] shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl text-[#1A2421]">Sign in</CardTitle>
              <p className="text-sm text-[#5C6B64]">Enter your credentials to access the dashboard</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div data-testid="login-error" className="bg-[#C45A41]/5 border border-[#C45A41]/20 text-[#C45A41] rounded-lg p-3 text-sm">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A2421]">Email</Label>
                  <Input
                    data-testid="login-email-input"
                    id="email"
                    type="email"
                    placeholder="you@restropulse.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-11 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1A2421]">Password</Label>
                  <Input
                    data-testid="login-password-input"
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="h-11 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] text-base"
                  />
                </div>
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white font-medium shadow-sm active:scale-[0.98] transition-all"
                >
                  {submitting ? 'Signing in...' : (<><LogIn className="w-4 h-4 mr-2" /> Sign In</>)}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-xs text-[#5C6B64] text-center mt-6">
            Contact your administrator for access credentials
          </p>

          {/* iter180g — public legal links for trust + Google verification */}
          <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-[#9AAA9F]">
            <a href="/legal/privacy" className="hover:text-[color:var(--color-primary)] transition-colors" data-testid="login-privacy-link">Privacy</a>
            <span className="text-[#D4D9D2]">·</span>
            <a href="/legal/terms" className="hover:text-[color:var(--color-primary)] transition-colors" data-testid="login-terms-link">Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}
